import React, { useState, useEffect } from 'react';
import { Trash2, RotateCcw, Calendar, FlaskConical, Award, Database, Clock, Search, X, AlertTriangle, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

const TYPE_META = {
  workshop: { label: 'Workshop', icon: Calendar, color: 'text-blue-700 bg-blue-50' },
  lab_request: { label: 'Lab Request', icon: FlaskConical, color: 'text-orange-700 bg-orange-50' },
  private_workshop_request: { label: 'Private Delivery Request', icon: Award, color: 'text-yellow-700 bg-yellow-50' },
  syndication_request: { label: 'Syndication Request', icon: Database, color: 'text-purple-700 bg-purple-50' },
};

const DeletedItemsPage = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [restoringKey, setRestoringKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.request('/admin/deleted-items');
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRestore = async (item) => {
    const key = `${item.type}-${item.id}`;
    setRestoringKey(key);
    try {
      const res = await api.request('/admin/deleted-items/restore', {
        method: 'POST',
        body: JSON.stringify({ type: item.type, id: item.id }),
      });
      toast.success(
        res.cascaded_lab_requests_restored > 0
          ? `Restored — ${res.cascaded_lab_requests_restored} linked lab request(s) also restored.`
          : 'Item restored successfully.'
      );
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to restore item.');
    }
    setRestoringKey(null);
  };

  const filtered = items.filter(i => {
    if (filterType !== 'all' && i.type !== filterType) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (i.title || '').toLowerCase().includes(q)
      || (i.subtitle || '').toLowerCase().includes(q)
      || (i.deleted_by || '').toLowerCase().includes(q);
  });
  const typeCounts = items.reduce((acc, i) => { acc[i.type] = (acc[i.type] || 0) + 1; return acc; }, {});

  const handlePurgeAll = async () => {
    setPurging(true);
    try {
      const res = await api.request('/admin/deleted-items/purge-now', { method: 'POST' });
      toast.success(res.message || 'Deleted Items purged.');
      setShowPurgeConfirm(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to purge Deleted Items.');
    }
    setPurging(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="deleted-items-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Trash2 size={22} className="text-red-300" /> Deleted Items</h1>
          <p className="text-gray-300 mt-1 text-sm">Recover deleted workshops and requests within 30 days of deletion</p>
        </div>
        <button onClick={load} className="px-4 py-2 border border-white/30 rounded-lg text-sm text-white hover:bg-white/10 transition-colors" data-testid="deleted-items-refresh">
          Refresh
        </button>
      </div>

      {user?.is_super_admin && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="purge-now-banner">
          <div className="flex items-start gap-2">
            <Flame size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Super Admin: Purge Now</p>
              <p className="text-xs text-red-600 mt-0.5">Permanently delete all {items.length} item(s) currently in the trash, right now — skips the normal 30-day wait. Cannot be undone.</p>
            </div>
          </div>
          <button
            onClick={() => setShowPurgeConfirm(true)}
            disabled={items.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="purge-all-now-button"
          >
            <Trash2 size={14} /> Purge All Now
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap justify-between">
        <div className="flex gap-2 flex-wrap">
          {['all', 'workshop', 'lab_request', 'private_workshop_request', 'syndication_request'].map(t => (
            <button key={t} onClick={() => setFilterType(t)} data-testid={`deleted-filter-${t}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === t ? 'bg-levelup-dark text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {t === 'all' ? `All (${items.length})` : `${TYPE_META[t].label} (${typeCounts[t] || 0})`}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search deleted items..."
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent/40 focus:border-levelup-accent"
            data-testid="deleted-items-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="deleted-items-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-16 text-center text-gray-500" data-testid="deleted-items-empty">
          <Trash2 size={40} className="mx-auto mb-3 text-gray-300" />
          {searchQuery.trim() || filterType !== 'all' ? (
            <>
              <p className="font-medium">No matching deleted items</p>
              <p className="text-sm text-gray-400 mt-1">Try a different search term or clear the filters above.</p>
            </>
          ) : (
            <>
              <p className="font-medium">Deleted Items is empty</p>
              <p className="text-sm text-gray-400 mt-1">Deleted workshops and requests will appear here for 30 days before permanent removal.</p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Type', 'Item', 'Deleted By', 'Deleted At', 'Expires In', 'Action'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(item => {
                const meta = TYPE_META[item.type] || TYPE_META.workshop;
                const Icon = meta.icon;
                const key = `${item.type}-${item.id}`;
                const isRestoring = restoringKey === key;
                return (
                  <tr key={key} className="hover:bg-gray-50 transition-colors" data-testid={`deleted-item-row-${key}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${meta.color}`}>
                        <Icon size={12} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900 max-w-[260px] truncate" title={item.title}>
                      {item.title}
                      <div className="text-xs text-gray-400 font-normal truncate">{item.subtitle}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{item.deleted_by || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{item.deleted_at ? new Date(item.deleted_at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${item.days_remaining <= 5 ? 'text-red-600' : 'text-gray-500'}`}>
                        <Clock size={12} /> {item.days_remaining} day{item.days_remaining === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleRestore(item)}
                        disabled={isRestoring}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-levelup-dark text-white rounded-lg text-xs font-semibold hover:bg-levelup-teal transition-colors disabled:opacity-50"
                        data-testid={`restore-btn-${key}`}
                      >
                        <RotateCcw size={13} className={isRestoring ? 'animate-spin' : ''} />
                        {isRestoring ? 'Restoring...' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showPurgeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="purge-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertTriangle size={22} />
              <h3 className="text-lg font-bold">Permanently purge everything?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This will immediately and permanently delete all <strong>{items.length}</strong> item(s)
              currently in Deleted Items — skipping the normal 30-day recovery window. This action
              <strong> cannot be undone</strong>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPurgeConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                data-testid="purge-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handlePurgeAll}
                disabled={purging}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="purge-confirm-submit"
              >
                <Trash2 size={14} className={purging ? 'animate-pulse' : ''} />
                {purging ? 'Purging...' : 'Yes, purge permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletedItemsPage;
